import { Offer } from "@core/offers/types";
import { trans } from "@mongez/localization";
import { OfferCard } from "../../offers/components/OfferCard";

interface OffersSectionProps {
  offers: Offer[];
  isLoading: boolean;
  error: string | null;
  onFetchOffers: () => void;
  hasLoaded: boolean;
}

export function OffersSection({
  offers,
  isLoading,
  error,
  onFetchOffers,
  hasLoaded,
}: OffersSectionProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary"></div>
          <p className="text-gray-600 dark:text-gray-300">
            {trans("offers.loadingOffers")}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="mb-4 text-red-500">
            <svg
              className="mx-auto h-12 w-12"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <p className="mb-4 text-gray-600 dark:text-gray-300">{error}</p>
          <button
            onClick={onFetchOffers}
            className="hover:bg-primary-dark rounded-lg bg-primary px-4 py-2 text-white transition-colors"
          >
            {trans("offers.tryAgain")}
          </button>
        </div>
      </div>
    );
  }

  if (offers.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="mb-4 text-gray-400">
            <svg
              className="mx-auto h-12 w-12"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
            {trans("offers.noOffersForLesson")}
          </h3>
          <p className="text-gray-600 dark:text-gray-300">
            {trans("offers.noOffersForLessonMessage")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-8 text-center">
        <h3 className="mb-2 text-xl font-bold text-gray-900 dark:text-gray-100">
          {trans("offers.offersForThisLesson")}
        </h3>
        <p className="text-gray-600 dark:text-gray-300">
          {trans("offers.offersForThisLessonMessage")}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {offers.map((offer) => (
          <OfferCard key={offer.id} offer={offer} />
        ))}
      </div>
    </div>
  );
}
